<template>
  <div>
    <!-- accordion -->
    <div
      :id="'expand-whatsapp-customization-accordion' + id"
      class="flex w-full cursor-pointer flex-row items-center justify-between gap-2 border px-5 transition-all"
      :class="{ 'border-b-[1px]': state.accordions[props.index].isOpen }"
    >
      <UICheckbox
        :id="'whatsapp-notification-receiver-checkbox-1' + id"
        v-model:checked="eventNotification.state.isActive"
        class="pb-5 pt-6 text-sm"
        @change="updateWhatsAppNotification(eventNotification.state)"
      >
        {{ whatsappReceiverLabels[notification.receiverType] }}
      </UICheckbox>
      <div
        v-if="eventNotification.state.isActive"
        class="flex w-2/3 justify-end pb-5 pt-6"
        role="button"
        tabindex="0"
        @click="handleAccordionClick"
        @keydown="handleAccordionClick"
      >
        <ChevronDownIcon
          v-if="!state.accordions[props.index].isOpen"
          class="h-5 w-5"
        />
        <ChevronUpIcon v-else class="h-5 w-5" />
      </div>
    </div>
    
    <!-- WhatsApp Notification configurator -->
    <div
      v-if="state.accordions[props.index].isOpen"
      class="flex w-full flex-col border border-t-0 bg-gray-25"
    >
      <div class="p-6">
        <!-- duration picker -->
        <div
          v-if="showDurationPicker"
          :id="'whatsapp-notification-duration-picker' + id"
          class="py-2"
        >
          <span class="font-medium text-gray-700">
            {{ t('eventNotification.whatsapp.durationLabel') }}
          </span>
          <div
            v-for="(d, i) in isFollowupNotifications
              ? eventNotification.state.afterTime
              : eventNotification.state.beforeTime"
            :key="'whatsapp-duration-picker-' + i"
            class="flex flex-row items-center space-x-3"
            :class="{ '!-mb-10 !-mt-5': i === 0, '!-my-10': i > 0 && i < 2 }"
          >
            <DurationPicker
              :id="'whatsapp-before-time-picker' + i + id"
              class="w-1/2"
              :value="String(d.timeOffset)"
              :unit="d.unit === 'minutes' ? 'mins' : d.unit"
              @on-value-change="
                value => {
                  d.timeOffset = value.duration
                  d.unit =
                    value.timeUnit === 'mins' ? 'minutes' : value.timeUnit
                }
              "
            />

            <span class="mt-5 flex items-center">{{
              isFollowupNotifications
                ? t('eventNotification.whatsapp.afterAppointmentEnds')
                : t('eventNotification.whatsapp.beforeAppointmentStarts')
            }}</span>
            <UIButton
              v-if="i > 0"
              id="remove-whatsapp-availability-hour"
              class="m-x-4 mt-4 text-red-600"
              :text="true"
              @click="removeHour(i)"
            >
              <Trash02Icon class="w-4 items-center" />
            </UIButton>
          </div>
          <div
            v-if="shouldEnableAddMore"
            class="!mt-4 w-[15%] py-4"
            role="button"
            tabindex="0"
            @click="addHour"
            @keydown="addHour"
          >
            <a
              use="outline"
              class="mt-5 cursor-pointer bg-white text-blue-500"
              size="large"
            >
              <PlusIcon class="mr-1 w-5" />
              {{ t('eventNotification.whatsapp.addMore') }}
            </a>
          </div>
        </div>

        <!-- WhatsApp template selector -->
        <div class="mb-[20px] flex flex-col gap-2">
          <div class="flex flex-row items-center gap-3">
            <div class="text-sm font-medium text-gray-700">
              {{ t('eventNotification.whatsapp.templateLabel') }}
            </div>

            <div
              class="flex cursor-pointer flex-row items-center gap-1"
              role="button"
              tabindex="0"
              @click="onClickRefreshWhatsAppTemplates"
              @keydown="onClickRefreshWhatsAppTemplates"
            >
              <RefreshCcw01Icon class="w-4 text-primary-500" />
              <div class="cursor-pointer text-sm text-primary-500">
                {{ t('whatsappTemplate.fetch_new_templates') }}
              </div>
            </div>
          </div>
          <UISelect
            :id="'whatsapp-template-selector' + id"
            :options="whatsappTemplateOptions"
            :placeholder="t('eventNotification.whatsapp.templatePlaceholder')"
            :value="selectedWhatsAppTemplateValue"
            :render-option="renderWhatsAppTemplateOption"
            filterable
            :loading="props.isRefreshingTemplates"
            clearable
            @update:value="onSelectWhatsAppTemplate"
          >
            <template #action>
              <UIButton
                :id="'create-whatsapp-template' + id"
                type="tertiary"
                class="w-full"
                size="small"
                @click="() => openWhatsAppTemplateCreator(false)"
              >
                <div class="flex flex-row items-center gap-2">
                  <PlusIcon class="mr-1 w-5 text-primary-700" />
                  {{ t('eventNotification.whatsapp.createNewTemplate') }}
                </div>
              </UIButton>
            </template>
          </UISelect>
          <!-- Template validation error message (shows below select) -->
          <div 
            v-if="eventNotification.state.isActive && !eventNotification.state.templateId"
            class="text-sm text-red-600"
          >
            {{ t('eventNotification.whatsapp.templateRequired', 'WhatsApp template is required when notifications are enabled') }}
          </div>
        </div>

        <!-- WhatsApp Template Preview -->
        <WhatsAppTemplatePreview
          :templateId="eventNotification.state.templateId"
          :template="selectedTemplate"
          :openEditModal="openEditModal"
          :isDefaultTemplate="isDefaultTemplate"
        />

        <!-- Additional Phone Numbers for "phoneNumbers" receiver type -->
        <UIForm v-if="eventNotification.state.receiverType === 'phoneNumbers'">
          <UIFormItem
            id="calendar-alert-whatsapp-phone-numbers"
            :label="t('eventNotification.whatsapp.alertPhoneNumbers')"
          >
            <UIInput
              id="calendar-alert-whatsapp-phone-numbers-input"
              v-model="additionalPhoneNumbers"
              :rows="4"
              type="textarea"
              :placeholder="t('eventNotification.whatsapp.alertPhoneNumbersPlaceholder')"
            />
          </UIFormItem>
        </UIForm>

        <!-- Test WhatsApp -->
        <UIForm
          id="test-whatsapp-form"
          ref="testWhatsAppFormRef"
          :rules="testWhatsAppRules"
          :model="eventNotification"
          class=""
        >
          <UIFormItem
            id="test-whatsapp-phone-number"
            :label="t('eventNotification.whatsapp.testWhatsAppLabel')"
            path="testPhoneNumber"
            class="w-full"
          >
            <UIInput
              :id="'test-whatsapp-phone-number' + id"
              v-model="eventNotification.testPhoneNumber.value"
              type="tel"
              :placeholder="t('eventNotification.whatsapp.testWhatsAppPlaceholder')"
            />
          </UIFormItem>
          <UIButton
            :id="'send-test-whatsapp' + id"
            type="default"
            :disabled="!isEnabledTestWhatsAppButton || isSendingWhatsApp"
            @click="sendTestWhatsApp"
            >{{ t('eventNotification.whatsapp.sendButtonLabel') }}</UIButton
          >
        </UIForm>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { EventNotificationService } from '@/class/services/EventNotificationService'
import WhatsappService from '@/class/services/WhatsappService'
import { useNotify } from '@/components/composition/notification'
import AppState, { contactLabelSingular, getWhatsAppDefaultTemplates } from '@/states/app'
import {
  EventNotification,
  eventNotificationState as state
} from '@/states/EventNotificationState'
import {
  NotificationConfig,
  NotificationType
} from '@/types/notification'
import { validatePhoneNumber } from '@/utils/validation'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  RefreshCcw01Icon,
  Trash02Icon
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UICheckbox,
  UIForm,
  UIFormItem,
  UIInput,
  UISelect
} from '@gohighlevel/ghl-ui'
import { computed, h, onMounted, PropType, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getDefaultWhatsAppTemplateLabel } from '../../../utils/default-notification-template'
import DurationPicker from '../DurationPicker.vue'
import WhatsAppTemplatePreview from './WhatsAppTemplatePreview.vue'
const { t } = useI18n()
const { sendNotification } = useNotify()
const testWhatsAppFormRef = ref()
const isSendingWhatsApp = ref(false)
const additionalPhoneNumbers = ref()
const whatsappTemplateOptions = ref<any[]>([])


// Computed property to help maintain template selection
const selectedWhatsAppTemplateValue = computed(() => {
  const templateId = eventNotification.state.templateId || ''
  
  // If templateId is empty, return empty
  if (!templateId) {
    return ''
  }
  
  // If no templates loaded yet, show empty (don't show raw template ID)
  if (!whatsappTemplateOptions.value || whatsappTemplateOptions.value.length === 0) {
    return ''  // Show empty until templates load, then proper name will be shown
  }
  
  // Check if the templateId exists in loaded options and is valid
  const templateExists = whatsappTemplateOptions.value.find(t => 
    t.value === templateId && !t.disabled && t.type !== 'section-header'
  )
  
  if (!templateExists) {
    eventNotification.state.templateId = ''
    return ''
  }
  
  return templateId  // Return ID, UISelect will show the proper name from options
})


// Render function for WhatsApp template options with sections
const renderWhatsAppTemplateOption = ({ node, option }) => {
  // Section header rendering
  if (option.type === 'section-header') {
    return h('div', {
      class: 'px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-200'
    }, option.label)
  }
  
  // Regular template option rendering
  if (option.type === 'pending') {
    // Pending template - disabled styling with approval status
    return h('div', {
      class: 'px-3 py-2 text-gray-400 cursor-not-allowed'
    }, [
      h('span', {}, `${option.label} (Pending Approval)`),
    ])
  }
  
  // Approved template - default node styling
  return node
}


// Computed property to get the full selected template object
const selectedTemplate = computed(() => {
  const templateId = eventNotification.state.templateId
  if (!templateId) return null
  
  const templateOption = whatsappTemplateOptions.value.find(t => t.value === templateId)
  if (!templateOption) return null
  
  // If the template option has a template property, return it
  // This ensures the preview gets the template in the expected format
  return templateOption.template || templateOption
})

// Computed property to check if selected template is a default template
const isDefaultTemplate = computed(() => {
  const templateId = eventNotification.state.templateId
  if (!templateId) return false
  
  const templateOption = whatsappTemplateOptions.value.find(t => t.value === templateId)
  return templateOption?.source === 'default'
})

defineExpose({
  validateForm,
})

const whatsappReceiverLabels = {
  contact: contactLabelSingular.value,
  assignedUser: t('eventNotification.whatsapp.receiverLabels.assignedUser'),
  phoneNumbers: t('eventNotification.whatsapp.receiverLabels.phoneNumbers'),
}

const props = defineProps({
  notification: {
    type: Object as PropType<NotificationConfig>,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  isNotificationsEnabled: {
    type: Boolean,
    default: true,
  },
  isRefreshingTemplates: {
    type: Boolean,
    default: false,
  },
})

watch(
  () => props.isNotificationsEnabled,
  () => {
    if (!props.isNotificationsEnabled) {
      state.accordions[props.index].isOpen = false
    }
  }
)

watch(
  () => additionalPhoneNumbers.value,
  (phoneNumbers) => {
    // Update additional phone numbers in the notification state
    const phoneList = (phoneNumbers || '').toString()
    eventNotification.state.additionalPhoneNumbers = phoneList
      .split(/[\s,]+/)
      .filter(phone => phone.trim() !== '' && validatePhoneNumber(phone))
  }
)

watch(
  () => AppState.whatsappNotificationTemplate.isUpdated,
  (newValue) => {
    if (newValue && AppState.whatsappNotificationTemplate.templateId) {
      onWhatsAppTemplateCreatorSuccess(AppState.whatsappNotificationTemplate.templateId)
    }
  }
)

// Watch for templates loaded in background (from AppState)
watch(
  () => AppState.whatsappTemplates,
  (newTemplates) => {
    if (newTemplates && newTemplates.length > 0) {
      // Templates loaded by parent component, update local options
      whatsappTemplateOptions.value = newTemplates
    }
  }
)

// Watch for template options changes and auto-populate templateId in notification object
watch(
  () => whatsappTemplateOptions.value,
  (newTemplates, oldTemplates) => {
    
    // Check if there's a pending template ID to select (from recent creation/edit)
    const pendingTemplateId = AppState.whatsappNotificationTemplate.templateId
    if (pendingTemplateId && newTemplates && newTemplates.length > 0) {
      
      const newTemplate = newTemplates.find(template => 
        template.value === pendingTemplateId && 
        !template.disabled && 
        template.type !== 'section-header'
      )
      
      if (newTemplate) {
        onSelectWhatsAppTemplate(pendingTemplateId)
        // Clear the pending template ID after successful selection
        AppState.whatsappNotificationTemplate.templateId = ''
        return
      }
    }
    
    // If templates just loaded (were empty, now have templates), auto-populate templateId
    if ((!oldTemplates || oldTemplates.length === 0) && newTemplates && newTemplates.length > 0) {
      
      // Auto-select template when API response returns
      autoSelectTemplate()
    }
    // If templates were refreshed and a default template is currently selected, re-fetch its full data
    else if (oldTemplates && oldTemplates.length > 0 && newTemplates && newTemplates.length > 0 && eventNotification.state.templateId) {
      const currentTemplate = newTemplates.find(opt => opt.value === eventNotification.state.templateId)
      if (currentTemplate && currentTemplate.source === 'default' && 
          (!currentTemplate.template?.components || currentTemplate.template.components.length === 0)) {
        fetchFullTemplateData(eventNotification.state.templateId, currentTemplate)
      }
    }
  }
)

const eventNotification = new EventNotification(props.notification)
state[props.notification.receiverType] = eventNotification

onMounted(async () => {
  eventNotification.state.isActive = props.notification.isActive
  // Convert array to comma-separated string for display in textarea
  const phoneNumbers = eventNotification.state.additionalPhoneNumbers
  additionalPhoneNumbers.value = Array.isArray(phoneNumbers) 
    ? phoneNumbers.join(', ') 
    : (phoneNumbers || '')
  
  
  await init()
})


const showDurationPicker = computed(() => {
  return [NotificationType.REMINDER, NotificationType.FOLLOWUP].includes(
    props.notification.notificationType
  )
})

const isFollowupNotifications = computed(() => {
  return props.notification.notificationType === NotificationType.FOLLOWUP
})

const isEnabledTestWhatsAppButton = computed(() => {
  return eventNotification.testPhoneNumber?.value
})


const id = computed(() => {
  return `-${props.notification._id}-${props.notification.receiverType}`
})

// Helper function to get the appropriate time array
const getTimeArray = () => {
  return isFollowupNotifications.value 
    ? eventNotification.state.afterTime 
    : eventNotification.state.beforeTime
}

const shouldEnableAddMore = computed(() => {
  const timeArray = getTimeArray()
  return timeArray && timeArray.length < 3
})

const testWhatsAppRules = {
  testPhoneNumber: {
    required: false,
    trigger: ['input', 'blur'],
    validator(_, value) {
      if (value?.value) {
        const phoneNumber = value.value.trim()
        
        // Allow custom variables in phone numbers
        if (phoneNumber.includes('{{')) {
          return true
        }
        
        // Check if phone number starts with + and has valid format
        if (!phoneNumber.startsWith('+')) {
          return new Error(t('eventNotification.whatsapp.phoneValidation.countryCodeRequired'))
        }
        
        // Remove + and check if remaining characters are all digits
        const digitsOnly = phoneNumber.slice(1)
        if (!/^\d+$/.test(digitsOnly)) {
          return new Error(t('eventNotification.whatsapp.phoneValidation.digitsOnly'))
        }
        
        // Check minimum length (should be at least 10 digits after country code for most countries)
        if (digitsOnly.length < 10) {
          return new Error(t('eventNotification.whatsapp.phoneValidation.tooShort'))
        }
        
        // Check maximum length (reasonable upper limit)
        if (digitsOnly.length > 15) {
          return new Error(t('eventNotification.whatsapp.phoneValidation.tooLong'))
        }
      }
      
      return true
    },
  },
}


async function init() {
  // Templates will be loaded by the parent tab component
  // The watcher on whatsappTemplateOptions will handle auto-selection when ready
  
  // Set up time defaults
  if (
    eventNotification.state.beforeTime?.length === 0 &&
    !isFollowupNotifications.value
  ) {
    eventNotification.state.beforeTime = [{ timeOffset: 10, unit: 'minutes' }]
  }
  if (
    eventNotification.state.afterTime?.length === 0 &&
    isFollowupNotifications.value
  ) {
    eventNotification.state.afterTime = [{ timeOffset: 1, unit: 'hours' }]
  }
}

// Auto-select template: either existing templateId from notification or default template from dropdown
const autoSelectTemplate = async () => {

  // SCENARIO 1: If notification has templateId, find it in combined templates (user + default) and auto-select
  if (eventNotification.state.templateId) {

    const existingTemplate = whatsappTemplateOptions.value.find(template => 
      template.value === eventNotification.state.templateId && 
      !template.disabled && 
      template.type !== 'section-header'
    )

    if (existingTemplate) {
      // Template found in dropdown, trigger selection to update UI
      onSelectWhatsAppTemplate(eventNotification.state.templateId)
      return
    } else {
      // Clear invalid templateId and proceed to scenario 2
      eventNotification.state.templateId = ''
    }
  }
  
  // SCENARIO 2: No templateId in notification, find matching default template using correct flow

  try {
    // Use the new helper function to find and fetch the default template
    const matchingDefault = await findMatchingDefaultTemplate()
    
    if (matchingDefault) {
      // Auto-populate the templateId in notification object
      eventNotification.state.templateId = matchingDefault.templateId
      
      // Update the dropdown option with the fetched template for preview
      const existingOption = whatsappTemplateOptions.value.find(opt => opt.value === matchingDefault.templateId)
      if (existingOption) {
        existingOption.template = matchingDefault.fullTemplate
      }
      
      // Trigger UI selection to update the preview with the fetched template body
      onSelectWhatsAppTemplate(matchingDefault.templateId)
    }
    // If no matching default template found, leave blank (no action needed)

  } catch (error) {
    console.error('[WhatsApp Template Auto-Selection] Error in default template flow:', error)
  }
}

// Helper function to find matching default template using the correct flow
const findMatchingDefaultTemplate = async () => {
  const notificationType = eventNotification.state.notificationType
  const receiverType = eventNotification.state.receiverType

  // Step 1: Get the hardcoded default template name from default-notification-template.ts
  const defaultTemplateName = getDefaultWhatsAppTemplateLabel(notificationType, receiverType)
  
  if (!defaultTemplateName) {
    return null
  }

  try {
    // Step 2: Get template-status response to find the template ID by name
    const defaultTemplates = await getWhatsAppDefaultTemplates()
    
    if (!defaultTemplates || defaultTemplates.length === 0) {
      return null
    }

    // Find template ID by matching the name
    const matchingTemplate = defaultTemplates.find(template => {
      const templateName = template.templateName || template.name
      return templateName === defaultTemplateName
    })

    if (!matchingTemplate) {
      return null
    }

    const templateId = matchingTemplate.templateId || matchingTemplate.id

    // Step 3: Fetch the full template body using GET template endpoint with the template ID
    const { WhatsappTemplateService } = await import('@/class/services/WhatsappService')
    const response = await WhatsappTemplateService.getTemplateById(AppState.locationId, templateId)
    const fullTemplate = response?.data

    if (!fullTemplate) {
      return null
    }

    // Return the complete template info for auto-selection
    return {
      templateId: templateId,
      templateName: fullTemplate.name || defaultTemplateName,
      fullTemplate: fullTemplate,
      source: 'default'
    }

  } catch (error) {
    console.error('[WhatsApp Template Auto-Selection] Error in default template lookup flow:', error)
    return null
  }
}

// Helper function to fetch full template data for default templates
const fetchFullTemplateData = async (templateId: string, templateOption: any) => {
  try {
    const { WhatsappTemplateService } = await import('@/class/services/WhatsappService')
    const response = await WhatsappTemplateService.getTemplateById(AppState.locationId, templateId)
    const fullTemplate = response?.data

    if (fullTemplate && fullTemplate.components) {
      // Update the dropdown option with the full template data
      templateOption.template = fullTemplate
    }
  } catch (error) {
    console.error('[WhatsApp Template] Error fetching full template data:', error)
  }
}

function onSelectWhatsAppTemplate(value: string) {
  // If value is empty/null, clear the selection
  if (!value || value.trim() === '') {
    eventNotification.state.templateId = ''
    return
  }
  
  const template = whatsappTemplateOptions.value.find(
    template => template.value === value
  )
  
  // Prevent selection of disabled (pending) templates or section headers
  if (template && (template.disabled || template.type === 'section-header')) {
    return
  }
  
  // Only proceed if we found a valid, approved template
  if (template && !template.disabled && template.type !== 'section-header') {
    eventNotification.state.templateId = template.value
    
    // If this is a default template without full components data, fetch it
    if (template.source === 'default' && (!template.template?.components || template.template.components.length === 0)) {
      fetchFullTemplateData(template.value, template)
    }
    
  } else {
    // If no valid template found (e.g., user searched for non-existent template),
    // don't update the selection - keep the current valid selection
    console.warn(t('eventNotification.whatsapp.errors.invalidTemplateSelection'), value)
    
  }
}

const onClickRefreshWhatsAppTemplates = () => {
  // Trigger refresh at the tab level to avoid duplicate API calls
  AppState.whatsappNotificationTemplate.isUpdated = true
}

const openWhatsAppTemplateCreator = async (isEdit: boolean = false) => {
  try {
    await mountWhatsAppTemplateCreator(isEdit)
  } catch (error) {
    console.error(t('eventNotification.whatsapp.errors.mountingCreator'), error)
  }
}

const mountWhatsAppTemplateCreator = async (isEdit: boolean = false) => {
  AppState.parentApp?.then(parent => {
    parent.emit('mount-whatsapp-template-app', {
      templateId: isEdit ? eventNotification.state.templateId : null,
      locationId: AppState.locationId,
      source: 'CALENDAR_NOTIFICATION',
      isEdit,
    })
  })
}

const onWhatsAppTemplateCreatorSuccess = async (templateId: string) => {
  // Store the template ID to select after templates refresh
  if (templateId) {
    AppState.whatsappNotificationTemplate.templateId = templateId
    // The template selection will happen automatically when templates are refreshed
    // via the watch on whatsappTemplateOptions
  }
}

const openEditModal = () => {
  openWhatsAppTemplateCreator(true)
}

function addHour() {
  const timeArray = getTimeArray()
  timeArray?.push({
    timeOffset: 10,
    unit: 'minutes',
  })
}

function removeHour(i: number) {
  const timeArray = getTimeArray()
  timeArray?.splice(i, 1)
}

async function updateWhatsAppNotification(data: NotificationConfig) {
  const { _id, isActive } = data
  if (!_id) {
    return
  }
  handleAccordionOnCheck(isActive)
  try {
    await EventNotificationService.updateEventNotifications({ _id, isActive })
    // API succeeded - update central state with the payload we sent
    const index = state.whatsappNotifications.findIndex(
      n => n._id === _id || n.receiverType === data.receiverType
    )
    if (index !== -1) {
      state.whatsappNotifications[index] = {
        ...state.whatsappNotifications[index],
        isActive,
      }
    }
  } catch (error) {
    // Revert local state on error
    eventNotification.state.isActive = !isActive
    console.error(t('eventNotification.whatsapp.errors.updateStatus'), error)
  }
}

async function sendTestWhatsApp() {
  try {
    await testWhatsAppFormRef.value.getForm().validate()
  } catch (error) {
    console.error(t('eventNotification.whatsapp.phoneValidation.invalid'))
    return
  }

  try {
    isSendingWhatsApp.value = true
    const { testPhoneNumber } = eventNotification
    const { body } = eventNotification.state

    // Extract template components for placeholders
    const components = selectedTemplate.value.components
    const getComponentPlaceholder = (type: string) => {
      const component = components.find(c => c.type === type)
      switch (type) {
        case 'HEADER':
          if (component?.format === 'IMAGE' || component?.format === 'VIDEO' || 
              component?.format === 'DOCUMENT' || component?.format === 'LOCATION') {
            return component?.customVariables || []
          } else {
            return component?.example?.header_text || []
          }
        case 'BODY':
          return component?.example?.body_text?.[0] || []
        case 'FOOTER':
          return component?.example?.footer_text || []
        case 'BUTTONS':
          return component?.buttons?.map(button => {
            if(button.type === 'URL') {
              return {
                ...button,
                customVariable: button.example?.[0] || '1234'
              }
            } else {
              return button
            }
          }) || []
        default:
          return []
      }
    }

    const whatsappService = new WhatsappService({
      to: testPhoneNumber.value,
      template: {
        name: selectedTemplate.value.name,
        language: selectedTemplate.value.language,
        placeholders: {
          header: JSON.parse(JSON.stringify(getComponentPlaceholder('HEADER'))),
          body: JSON.parse(JSON.stringify(getComponentPlaceholder('BODY'))),
          footer: JSON.parse(JSON.stringify(getComponentPlaceholder('FOOTER'))),
          buttons: JSON.parse(JSON.stringify(getComponentPlaceholder('BUTTONS'))),
        },
      }
    })
    await whatsappService.sendTestWhatsapp()
    
    // Show success notification
    sendNotification({
      type: 'success',
      title: t('eventNotification.whatsapp.testWhatsAppSuccessTitle'),
      description: t('eventNotification.whatsapp.testWhatsAppSuccessDesc', { phone: testPhoneNumber.value }),
      icon: 'CheckCircleIcon'
    })
  } catch (error) {
    console.error('Error in sending test WhatsApp', error)
    
    // Show error notification
    sendNotification({
      type: 'error',
      title: t('eventNotification.whatsapp.testWhatsAppErrorTitle'),
      description: t('eventNotification.whatsapp.testWhatsAppErrorDesc'),
      icon: 'AlertCircleIcon'
    })
  } finally {
    isSendingWhatsApp.value = false
  }
}

function handleAccordionClick() {
  if (!props.isNotificationsEnabled || !eventNotification.state.isActive) {
    return
  }

  if (!state.accordions[props.index].isOpen) {
    openAccordion(props.index)
  } else {
    closeAccordion(props.index)
  }
}

async function handleAccordionOnCheck(isChecked: boolean) {
  state.accordions[props.index].isChecked = isChecked
  const accordion = state.accordions[props.index]
  if (accordion.isChecked) {
    openAccordion(props.index)
  } else {
    closeAccordion(props.index)
  }
}

function openAccordion(index) {
  if (index === null) return
  
  // Close previously opened accordion
  if (state.lastOpenedAccordion !== null && state.lastOpenedAccordion !== index) {
    state.accordions[state.lastOpenedAccordion].isOpen = false
  }
  
  state.accordions[index].isOpen = true
  state.lastOpenedAccordion = index
}

function closeAccordion(index) {
  if (index === null) return
  
  state.accordions[index].isOpen = false
  if (state.lastOpenedAccordion === index) {
    state.lastOpenedAccordion = null
  }
}

function validateForm() {
  return new Promise((resolve, reject) => {
    // First, validate the test phone form
    const testFormValidation = testWhatsAppFormRef?.value?.getForm()?.validate()
    
    // Check if WhatsApp notification is enabled and template is selected
    if (eventNotification.state.isActive && !eventNotification.state.templateId) {
      // Notification is enabled but no template selected - validation fails
      reject(new Error('WhatsApp template must be selected when notification is enabled'))
      return
    }
    
    // If test form validation exists, wait for it
    if (testFormValidation) {
      testFormValidation
        .then(() => resolve(true))
        .catch((error) => reject(error))
    } else {
      // No test form validation needed, resolve successfully
      resolve(true)
    }
  })
}

</script>

